import React, {useMemo, useState, useEffect, useCallback} from "react"
import {Card, Col, Container, Form, Row, Alert, Button, Badge} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../Common/BreadCrumb";
import DataTable from "../../../Common/DataTable";
import {dashboardAPI} from "../../../services/api";
import {CreateStoreGroup} from "../Components/CreateStoreGroup";
import {EditStoreGroup} from "../Components/EditStoreGroup";

interface StoreGroup {
    id: string;
    name: string;
    description?: string;
    active: boolean;
    companyId?: string;
    createdOn?: string;
    stores?: any[];
}

const StoreGroupList = () => {
    const {t} = useTranslation();
    document.title = t('StoreGroupList.title') || 'Store Groups';
    
    // Check if user is demo user
    const isDemoUser = useMemo(() => {
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) return false;
        try {
            const userData = JSON.parse(authUser);
            const token = userData.token || userData.access_token;
            return token?.startsWith('demo-token-') || userData.email === 'admin@demo.com.tr';
        } catch {
            return false;
        }
    }, []);

    const [data, setData] = useState<StoreGroup[]>([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedStoreGroup, setSelectedStoreGroup] = useState<StoreGroup | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Handle window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch store groups from backend for real users
    useEffect(() => {
        if (isDemoUser) {
            // Use mock data for demo users
            const mockStoreGroups = [
                { id: 'group-1', name: 'Istanbul Bölgesi', description: 'Istanbul mağazaları', active: true, stores: [] },
                { id: 'group-2', name: 'Ankara Bölgesi', description: 'Ankara mağazaları', active: true, stores: [] },
                { id: 'group-3', name: 'İzmir Bölgesi', description: 'İzmir mağazaları', active: true, stores: [] }
            ];
            setData(mockStoreGroups);
            return;
        }

        const fetchStoreGroups = async () => {
            setIsLoading(true);
            setError(null);
            try {
                console.log('📋 Fetching store groups from backend...');
                const response: any = await dashboardAPI.getStoreGroups();
                
                // Normalize response - handle both AxiosResponse and direct data
                const storeGroups = Array.isArray(response) ? response : (response?.data || []);
                
                console.log('✅ Store groups received:', storeGroups.length, 'store groups');
                
                // Map backend StoreGroupDTO to frontend StoreGroup format
                const mappedStoreGroups: StoreGroup[] = storeGroups.map((group: any) => ({
                    id: group.id || '',
                    name: group.name || '',
                    description: group.description || '',
                    active: group.active !== undefined ? group.active : true,
                    companyId: group.companyId,
                    createdOn: group.createdOn || new Date().toISOString(),
                    stores: group.stores || []
                }));
                
                setData(mappedStoreGroups);
            } catch (error: any) {
                console.error('❌ Failed to fetch store groups:', error);
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch store groups');
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStoreGroups();
    }, [isDemoUser]);

    const handleCreate = () => {
        setSelectedStoreGroup(null);
        setCreateModalOpen(true);
    };

    const handleEdit = (storeGroup: StoreGroup) => {
        setSelectedStoreGroup(storeGroup);
        setEditModalOpen(true);
    };

    const handleToggleActive = useCallback(async (storeGroup: StoreGroup, newActiveState: boolean) => {
        if (isDemoUser) {
            // For demo, just update local state
            const newData = [...data];
            const index = newData.findIndex(g => g.id === storeGroup.id);
            if (index !== -1) {
                newData[index].active = newActiveState;
                setData(newData);
            }
            return;
        }

        try {
            // Optimistically update UI
            const newData = [...data];
            const index = newData.findIndex(g => g.id === storeGroup.id);
            if (index !== -1) {
                newData[index].active = newActiveState;
                setData(newData);
            }

            // Update on backend
            await dashboardAPI.updateStoreGroup(storeGroup.id, {
                active: newActiveState
            });
        } catch (error: any) {
            console.error('Failed to update store group active status:', error);
            // Revert on error
            const newData = [...data];
            const index = newData.findIndex(g => g.id === storeGroup.id);
            if (index !== -1) {
                newData[index].active = !newActiveState;
                setData(newData);
            }
            alert(error?.response?.data?.message || error?.message || 'Failed to update store group status');
        }
    }, [isDemoUser, data]);

    const handleDelete = useCallback(async (storeGroup: StoreGroup) => {
        if (!window.confirm(t('StoreGroupList.confirm_delete') || `Are you sure you want to delete "${storeGroup.name}"?`)) {
            return;
        }

        if (isDemoUser) {
            // For demo, just update local state
            const newData = data.filter(g => g.id !== storeGroup.id);
            setData(newData);
            return;
        }

        try {
            await dashboardAPI.deleteStoreGroup(storeGroup.id);
            
            // Update local state
            const newData = data.filter(g => g.id !== storeGroup.id);
            setData(newData);
        } catch (error: any) {
            console.error('Failed to delete store group:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete store group';
            if (error?.response?.status === 400) {
                alert(errorMessage + ' ' + (t('StoreGroupList.cannot_delete_with_stores') || '(Cannot delete store group that contains stores)'));
            } else {
                alert(errorMessage);
            }
        }
    }, [isDemoUser, data, t]);

    const columns = useMemo(() => {
        const cols: any[] = [
            {
                Header: t('StoreGroupList.name') || 'Name',
                accessor: "name",
                Cell: ({row}: any) => {
                    const group = data[row.index];
                    return (
                        <div>
                            <strong>{group.name}</strong>
                            {group.description && (
                                <div className="text-muted small mt-1">{group.description}</div>
                            )}
                        </div>
                    );
                }
            },
            {
                Header: t('StoreGroupList.stores') || 'Stores',
                accessor: "stores",
                Cell: ({row}: any) => {
                    const group = data[row.index];
                    const storeCount = group.stores?.length || 0;
                    return (
                        <Badge bg="info">{storeCount}</Badge>
                    );
                }
            },
            {
                Header: t('StoreGroupList.active') || 'Active',
                accessor: "active",
                Cell: ({row, value}: any) => {
                    const group = data[row.index];
                    return (
                        <div className="form-check form-switch form-switch-custom form-switch-primary">
                            <Form.Check 
                                onChange={() => handleToggleActive(group, !value)} 
                                type="checkbox" 
                                role="switch" 
                                id={`store-group-switch-${group.id}`} 
                                checked={value}
                                disabled={isLoading}
                            />
                        </div>
                    );
                },
            },
            {
                Header: t('StoreGroupList.actions') || 'Actions',
                accessor: "actions",
                Cell: ({row}: any) => {
                    const group = data[row.index];
                    return (
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEdit(group)}
                            >
                                {t('StoreGroupList.edit') || 'Edit'}
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(group)}
                            >
                                {t('StoreGroupList.delete') || 'Delete'}
                            </Button>
                        </div>
                    );
                }
            }
        ];
        
        // Add created_on only on desktop
        if (!isMobile) {
            cols.splice(-1, 0, {
                Header: t('StoreGroupList.created_on') || 'Created On',
                accessor: "createdOn",
                Cell: ({value}: any) => {
                    if (!value) return '-';
                    const date = typeof value === 'string' ? new Date(value) : value;
                    return date.toLocaleDateString('tr-TR');
                }
            });
        }
        
        return cols;
    }, [data, t, isLoading, isMobile, handleToggleActive, handleDelete]);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb
                        items={[
                            {label: t('Dashboard.header') || 'Dashboard', path: "/"},
                            {label: t('StoreGroupList.page_title') || 'Store Groups', path: "/store-groups"}
                        ]}
                    />
                    {error && (
                        <Row>
                            <Col>
                                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                    {error}
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="card-title mb-0">
                                            {t('StoreGroupList.page_title') || 'Store Groups'}
                                        </h4>
                                        <Button
                                            variant="primary"
                                            onClick={handleCreate}
                                            disabled={isLoading}
                                        >
                                            <i className="las la-plus me-1"></i>
                                            {t('StoreGroupList.create') || 'Create Store Group'}
                                        </Button>
                                    </div>
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <DataTable
                                            columns={columns}
                                            data={data}
                                            title={t('StoreGroupList.search_store_group') || 'Search Store Groups'}
                                            editable={false}
                                            deletable={false}
                                            alertsDeletable={false}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            <CreateStoreGroup
                show={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSave={async () => {
                    setCreateModalOpen(false);
                    // Refresh data
                    const response: any = await dashboardAPI.getStoreGroups();
                    const storeGroups = Array.isArray(response) ? response : (response?.data || []);
                    const mappedStoreGroups: StoreGroup[] = storeGroups.map((group: any) => ({
                        id: group.id || '',
                        name: group.name || '',
                        description: group.description || '',
                        active: group.active !== undefined ? group.active : true,
                        companyId: group.companyId,
                        createdOn: group.createdOn || new Date().toISOString(),
                        stores: group.stores || []
                    }));
                    setData(mappedStoreGroups);
                }}
            />

            <EditStoreGroup
                show={editModalOpen}
                storeGroup={selectedStoreGroup}
                onClose={() => {
                    setEditModalOpen(false);
                    setSelectedStoreGroup(null);
                }}
                onSave={async () => {
                    setEditModalOpen(false);
                    setSelectedStoreGroup(null);
                    // Refresh data
                    const response: any = await dashboardAPI.getStoreGroups();
                    const storeGroups = Array.isArray(response) ? response : (response?.data || []);
                    const mappedStoreGroups: StoreGroup[] = storeGroups.map((group: any) => ({
                        id: group.id || '',
                        name: group.name || '',
                        description: group.description || '',
                        active: group.active !== undefined ? group.active : true,
                        companyId: group.companyId,
                        createdOn: group.createdOn || new Date().toISOString(),
                        stores: group.stores || []
                    }));
                    setData(mappedStoreGroups);
                }}
            />
        </React.Fragment>
    );
};

export default StoreGroupList;

